import { authenticatedAPI } from '../utils/authApi';

class DashboardApiService {
  constructor() {
    this.apiName = 'devices'; // Using the same API name as device connections
  }

  async getUserStats() {
    try {
      const response = await authenticatedAPI.get(this.apiName, '/analytics/personal');
      return response;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  }

  async getActivityData(period = '7d') {
    try {
      const response = await authenticatedAPI.get(this.apiName, '/analytics/activity', {
        queryStringParameters: { period }
      });
      return response;
    } catch (error) {
      console.error('Error fetching activity data:', error);
      throw error;
    }
  }

  async getTodayStats() {
    try {
      const response = await authenticatedAPI.get(this.apiName, '/analytics/today');
      return response;
    } catch (error) {
      console.error('Error fetching today stats:', error);
      // Return default values if API fails
      return {
        steps: 0,
        goal: 10000,
        distance: 0,
        calories: 0,
        lastSync: null
      };
    }
  }

  async getWeeklyProgress() {
    try {
      const response = await authenticatedAPI.get(this.apiName, '/analytics/weekly');
      return response;
    } catch (error) {
      console.error('Error fetching weekly progress:', error);
      throw error;
    }
  }

  async getRecentActivity(limit = 10) {
    try {
      const response = await authenticatedAPI.get(this.apiName, '/analytics/recent', {
        queryStringParameters: { limit: limit.toString() }
      });
      return response;
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      throw error;
    }
  }

  async getUserStepsData() {
    try {
      const response = await authenticatedAPI.get(this.apiName, '/analytics/today');
      return response;
    } catch (error) {
      console.error('Error fetching steps data:', error);
      // Return fallback data if API fails
      return {
        steps: 0,
        goal: 10000,
        distance: 0,
        calories: 0,
        lastSync: null
      };
    }
  }

  async getWeeklyStats() {
    try {
      const response = await authenticatedAPI.get(this.apiName, '/analytics/weekly');
      return response;
    } catch (error) {
      console.error('Error fetching weekly stats:', error);
      // Return fallback data
      return {
        weeklyAverage: 0,
        weeklyTotal: 0,
        totalAchievements: 0,
        dailyGoal: 10000
      };
    }
  }

  async getUserAchievements() {
    try {
      const response = await authenticatedAPI.get(this.apiName, '/analytics/personal');
      return response;
    } catch (error) {
      console.error('Error fetching achievements:', error);
      return { achievements: 0, totalAchievements: 0 };
    }
  }

  async getCurrentChallenge() {
    try {
      // For now, use the personal analytics endpoint which includes challenge info
      const response = await authenticatedAPI.get(this.apiName, '/analytics/personal');
      // Extract challenge information from the personal stats if available
      if (response && response.current_challenge) {
        return response.current_challenge;
      }
      return null;
    } catch (error) {
      console.error('Error fetching current challenge:', error);
      // Return null if user isn't in a challenge
      return null;
    }
  }

  async getUserChallenges() {
    try {
      // Use the challenges API to get user's active challenges
      const response = await authenticatedAPI.get('challenges', '/challenges', {
        queryStringParameters: { my_challenges: 'true' }
      });
      const challenges = response.challenges || response.data || response;
      
      // Filter out challenges that ended more than 7 days ago
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      return challenges.filter(challenge => {
        if (!challenge.end_date) return true; // Include challenges without end date
        const endDate = new Date(challenge.end_date);
        return endDate >= sevenDaysAgo; // Include challenges that ended within last 7 days
      });
    } catch (error) {
      console.error('Error fetching user challenges:', error);
      // Return empty array if API fails
      return [];
    }
  }

  async getFriendsLeaderboard() {
    try {
      // Get today's friend activity rankings from friends API
      const response = await authenticatedAPI.get('devices', '/friends/daily-steps');
      return response.leaderboard || response.data || response;
    } catch (error) {
      console.error('Error fetching friends leaderboard:', error);
      // Return empty array on error to let component handle empty state
      return [];
    }
  }

  async getDashboardSummary() {
    try {
      // Fetch multiple dashboard data sources in parallel
      const [stats, todayStats, weeklyProgress, recentActivity] = await Promise.all([
        this.getUserStats().catch(() => null),
        this.getTodayStats().catch(() => ({ steps: 0, goal: 10000 })),
        this.getWeeklyProgress().catch(() => []),
        this.getRecentActivity(5).catch(() => [])
      ]);

      return {
        stats,
        todayStats,
        weeklyProgress,
        recentActivity: recentActivity.activities || recentActivity || []
      };
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      throw error;
    }
  }
}

const dashboardApiService = new DashboardApiService();
export default dashboardApiService;